import { Table } from "@/components/shared/ui/RegistryTable";
import { useGetValidatedPublicProjectPagination } from "@/hooks/registry/useGetValidatedPublicProjectPagination";
import { PublicProjectType, RegistryPageProjectType } from "@/types/publicProject";
import { useMemo } from "react";
import { FullPageLoader } from "../shared/ui/Loader";
import PaginationTwo from "../shared/ui/PaginationTwo";

interface Props {
	hidePagination?: boolean;
	status: "VALIDATED" | "IN_VALIDATION";
}

const RegistryProjectTableWrapper = ({ hidePagination = false, status }: Props) => {
	const { validatedPublicProjects, pagination } = useGetValidatedPublicProjectPagination({
		status: [status] as string[],
	});
	const { data: publicProjectListData, isLoading } = validatedPublicProjects;
	const { changeOffset } = pagination;

	const data: RegistryPageProjectType[] | undefined = publicProjectListData?.projects?.map(
		(publicProject: PublicProjectType) => ({
			id: publicProject.id,
			project: publicProject.title,
			developer: publicProject.originator,
			sectors: [publicProject.sectoralScope.primary, publicProject.sectoralScope.secondary],
			location: publicProject.locations.map((location) => location.name),
			creditsAvailable: (publicProject.availableCredits && parseInt(publicProject.availableCredits)) || 0,
			creditIssued: (publicProject.issuedCredits && parseInt(publicProject.issuedCredits)) || 0,
			status: publicProject.status,
		}),
	);

	const showCredits = status === "VALIDATED";

	const cols = useMemo(() => {
		const baseCols = [
			{
				header: "ID",
				cell: (row: any) => {
					return <div className="max-w-2 flex items-center">{row.row.original.id}</div>;
				},
				accessorKey: "id",
			},
			{
				header: "Project",
				cell: (row: any) => {
					return (
						<div className="flex w-full min-w-[220px] items-center whitespace-normal break-words">
							{row.row.original.project}
						</div>
					);
				},
				accessorKey: "projectName",
			},
			{
				header: "Project Developer",
				cell: (row: any) => {
					return (
						<div className="flex min-w-[220px] items-center space-x-4 whitespace-normal break-words">
							{row.row.original.developer}
						</div>
					);
				},
				accessorKey: "projectDeveloper",
			},
			{
				header: "Sectoral Scope",
				cell: (row: any) => {
					return (
						<div className="flex flex-col items-start justify-start gap-y-2">
							{row.row.original.sectors.map((sector: string, index: number) => {
								return (
									<div
										key={index}
										className={`${
											index === 0 ? "bg-nightSky text-white" : "border-[1px] border-nightSky"
										} w-fit rounded-2xl px-2 py-1 !text-xs`}
									>
										{sector}
									</div>
								);
							})}
						</div>
					);
				},
				accessorKey: "sectoralScope",
			},
			{
				header: "Location",
				cell: (row: any) => {
					return (
						<div className="flex min-w-[120px] flex-col items-start justify-start gap-y-1 whitespace-normal break-words">
							{row.row.original.location.length > 0
								? row.row.original.location.map((location: string, index: number) => {
										return <div key={index}>{location}</div>;
								  })
								: "N/A"}
						</div>
					);
				},
				accessorKey: "location",
			},
		];

		const creditCols = showCredits
			? [
					{
						header: () => {
							return <div className="w-full text-center">Credits Available</div>;
						},
						cell: (row: any) => {
							return (
								<div className="flex items-center space-x-4">
									<div className="w-full text-right">{row.row.original.creditsAvailable.toLocaleString("en-US")}</div>
								</div>
							);
						},
						accessorKey: "creditsAvailable",
					},
					{
						header: () => {
							return <div className="w-full text-center">Credits Issued</div>;
						},
						cell: (row: any) => {
							return (
								<div className="flex items-center space-x-4">
									<div className="w-full text-right">{row.row.original.creditIssued.toLocaleString("en-US")}</div>
								</div>
							);
						},
						accessorKey: "creditIssued",
					},
			  ]
			: [];

		return [...baseCols, ...creditCols];
	}, [showCredits]);

	if (isLoading) {
		return <FullPageLoader />;
	}

	return (
		<div>
			{(!data || data.length == 0) && (
				<div className="flex items-center justify-center py-20">
					<p>{status == "VALIDATED" ? "No Projects Yet." : "No Projects In Validation Currently"}</p>
				</div>
			)}

			{data && data.length > 0 && (
				<div>
					{!hidePagination && data.length > 9 && (
						<div className="mt-8 flex w-full justify-end py-0 pb-0 pr-0 md:px-8">
							<PaginationTwo
								count={parseInt(publicProjectListData?.count!!)}
								changeOffset={changeOffset}
								offset={parseInt(publicProjectListData?.offset!!)}
								limit={parseInt(publicProjectListData?.limit!!)}
							/>
						</div>
					)}
					<Table data={data!!} columns={cols} />
				</div>
			)}
		</div>
	);
};

export default RegistryProjectTableWrapper;
